<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>Edit Stock Log</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Product Selection -->
            <v-col sm="4">
              <v-autocomplete
                v-model="selectedProduct"
                :items="products"
                label="Select Product *"
                outlined
                item-text="name"
                item-value="id"
                prepend-inner-icon="mdi-package"
                :error-messages="errors.product_id"
                @change="onProductChange"
                clearable
                :no-data-text="products.length === 0 ? 'Loading products...' : 'No products available'"
              ></v-autocomplete>
            </v-col>

            <!-- Variant Selection -->
            <v-col sm="4">
              <v-autocomplete
                v-model="form.variant_id"
                :items="productVariants"
                label="Select Product Variant *"
                outlined
                :item-text="formatVariantName"
                item-value="id"
                prepend-inner-icon="mdi-package-variant"
                :error-messages="errors.variant_id"
                :disabled="!selectedProduct"
                @change="onVariantChange"
                clearable
                :no-data-text="!selectedProduct ? 'Select a product first' : 'No variants for this product'"
              ></v-autocomplete>
            </v-col>

            <!-- Action Type -->
            <v-col sm="4">
              <v-autocomplete
                ref="action_type"
                outlined
                v-model="form.action_type"
                :items="actionTypeItems"
                label="Action Type *"
                prepend-inner-icon="mdi-swap-horizontal"
                clearable
                :error-messages="errors.action_type"
              ></v-autocomplete>
            </v-col>

            <!-- Quantity -->
            <v-col sm="4">
              <v-text-field
                v-model="form.quantity"
                outlined
                label="Quantity *"
                prepend-inner-icon="mdi-numeric"
                type="number"
                :error-messages="errors.quantity"
              ></v-text-field>
            </v-col>

            <!-- Remarks -->
            <v-col sm="8">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                prepend-inner-icon="mdi-note-text"
                rows="3"
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>

            <!-- Is Active Switch -->
            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>

          <!-- Selected Variant Details Card -->
          <v-card v-if="selectedVariant" class="mb-5" elevation="2" outlined>
            <v-card-title class="subtitle-1">Selected Variant Information</v-card-title>
            <v-card-text>
              <v-row>
                <v-col sm="3">
                  <strong>Product:</strong> {{ getProductName() }}
                </v-col>
                <v-col sm="3">
                  <strong>Variant:</strong> {{ selectedVariant.color }} - {{ selectedVariant.size }}
                </v-col>
                <v-col sm="3">
                  <strong>Price:</strong> ${{ selectedVariant.price || '0.00' }}
                </v-col>
                <v-col sm="3">
                  <strong>Current Stock:</strong>
                  <v-chip small :color="selectedVariant.quantity > 0 ? 'green' : 'red'" text-color="white">
                    {{ selectedVariant.quantity || 0 }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Stock Preview Card -->
          <v-card v-if="selectedVariant && form.action_type && form.quantity" class="mb-5" elevation="2" outlined>
            <v-card-title class="subtitle-1">Stock Transaction Update Preview</v-card-title>
            <v-card-text>
              <v-row>
                <v-col sm="3">
                  <strong>Current Stock:</strong>
                  <v-chip small color="blue" text-color="white">
                    {{ selectedVariant.quantity || 0 }}
                  </v-chip>
                </v-col>
                <v-col sm="3">
                  <strong>Old Transaction:</strong>
                  <v-chip small color="grey" text-color="white">
                    {{ getOldTransactionPrefix() }}{{ originalForm.quantity || 0 }}
                  </v-chip>
                </v-col>
                <v-col sm="3">
                  <strong>New Transaction:</strong>
                  <v-chip small :color="getTransactionColor()" text-color="white">
                    {{ getTransactionPrefix() }}{{ form.quantity }}
                  </v-chip>
                </v-col>
                <v-col sm="3">
                  <strong>Final Stock:</strong>
                  <v-chip small :color="getNewQuantityColor()" text-color="white">
                    {{ calculateNewQuantity() }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Log Information Card -->
          <v-card class="mb-5" elevation="2" outlined>
            <v-card-title class="subtitle-1">Log Information</v-card-title>
            <v-card-text>
              <v-row>
                <v-col sm="4">
                  <strong>Created By:</strong> {{ form.created_by ? form.created_by.user_name : '' }}
                </v-col>
                <v-col sm="4">
                  <strong>Created At:</strong> {{ formatDate(form.created_at) }}
                </v-col>
                <v-col sm="4">
                  <strong>Updated At:</strong> {{ formatDate(form.updated_at) }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <v-row>
            <v-col>
              <v-btn
                :loading="isLoading"
                :disabled="isLoading"
                color="primary"
                rounded
                dark
                @click="save"
              >
                Update Stock Log
              </v-btn>
              <v-btn
                :loading="isDeleting"
                :disabled="isDeleting"
                color="error"
                rounded
                dark
                class="ml-3"
                @click="deleteLog"
              >
                Delete Stock Log
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Success Dialog Box -->
      <v-dialog v-model="is_updated" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Stock log updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="handleSuccessClose">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: red; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-alert
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: red">Warning!</h1>
                      <br />
                      <h3>Are you sure you want to delete this stock log?</h3>
                      <p class="mt-2">This will reverse the stock quantity changes.</p>
                      <br />
                      <v-btn color="grey" @click="showDeleteDialog = false" class="mr-3">
                        Cancel
                      </v-btn>
                      <v-btn color="error" @click="confirmDelete">
                        Yes, Delete
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "StockLogEditPage",
  data() {
    return {
      form: {},
      originalForm: {},
      errors: {},
      selectedProduct: null,
      products: [],
      productVariants: [],
      selectedVariant: null,
      actionTypeItems: [
        { value: 1, text: "Add" },
        { value: 2, text: "Remove" },
        { value: 3, text: "Assign to Distributor" },
        { value: 4, text: "Assign to Seller" },
        { value: 5, text: "Assign to Franchise" },
        { value: 6, text: "Customer Purchase" },
        { value: 7, text: "Stock Refill" },
      ],
      isLoading: false,
      isDeleting: false,
      is_updated: false,
      showDeleteDialog: false,
    };
  },
  mounted() {
    this.getProducts();
    this.getData();
  },
  methods: {
    async getProducts() {
      try {
        const response = await axios.get("/products");
        this.products = response.data.data || [];
      } catch (error) {
        console.log("Error fetching products:", error);
      }
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.id;
      try {
        const response = await axios.get(`/stock_logs/${encryptedId}`);
        this.form = response.data.data;
        this.originalForm = {...response.data.data};

        // Set the selected product based on the variant
        if (this.form.variant && this.form.variant.product_id) {
          this.selectedProduct = this.form.variant.product_id;

          // Load variants for the selected product
          const product = this.products.find(p => p.id === this.selectedProduct);
          if (product && product.product_variants) {
            this.productVariants = product.product_variants;
          }
        } else if (this.form.variant_id) {
          // Try to find the product from the variant
          for (let product of this.products) {
            if (product.product_variants) {
              const variant = product.product_variants.find(v => v.id === this.form.variant_id);
              if (variant) {
                this.selectedProduct = product.id;
                this.productVariants = product.product_variants;
                break;
              }
            }
          }
        }

        this.onVariantChange();
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        this.isLoading = false;
      }
    },
    onProductChange() {
      // Reset variant selection
      this.form.variant_id = null;
      this.productVariants = [];
      this.selectedVariant = null;

      // Clear variant error when product changes
      if (this.errors.variant_id) {
        this.$set(this.errors, 'variant_id', null);
      }

      if (!this.selectedProduct) return;

      // Find the selected product
      const product = this.products.find(p => p.id === this.selectedProduct);
      if (product && product.product_variants) {
        this.productVariants = product.product_variants;
      }
    },
    onVariantChange() {
      if (this.form.variant_id) {
        // First try to find in productVariants
        this.selectedVariant = this.productVariants.find(v => v.id === this.form.variant_id);

        // If not found and we have variant data from the form, use that
        if (!this.selectedVariant && this.form.variant) {
          this.selectedVariant = this.form.variant;
        }
      } else {
        this.selectedVariant = null;
      }
    },
    formatVariantName(variant) {
      let name = [];
      if (variant.color) name.push(`Color: ${variant.color}`);
      if (variant.size) name.push(`Size: ${variant.size}`);
      if (variant.price) name.push(`$${variant.price}`);
      if (variant.quantity !== undefined) name.push(`Stock: ${variant.quantity}`);

      return name.length > 0 ? name.join(" | ") : "Variant";
    },
    getProductName() {
      if (!this.selectedProduct) return "";
      const product = this.products.find(p => p.id === this.selectedProduct);
      return product ? product.name : "";
    },
    calculateNewQuantity() {
      if (!this.selectedVariant || !this.form.quantity || !this.form.action_type) {
        return this.selectedVariant ? this.selectedVariant.quantity : 0;
      }

      const currentQty = parseInt(this.selectedVariant.quantity) || 0;
      const oldQty = parseInt(this.originalForm.quantity) || 0;
      const newQty = parseInt(this.form.quantity) || 0;
      const oldActionType = this.originalForm.action_type;
      const newActionType = this.form.action_type;

      let adjustedQty = currentQty;

      // Reverse the original transaction
      if ([1, 7].includes(oldActionType)) {
        adjustedQty -= oldQty;
      } else {
        adjustedQty += oldQty;
      }

      // Apply the new transaction
      if ([1, 7].includes(newActionType)) {
        adjustedQty += newQty;
      } else {
        adjustedQty -= newQty;
      }

      return adjustedQty < 0 ? 0 : adjustedQty;
    },
    getNewQuantityColor() {
      const newQty = this.calculateNewQuantity();
      if (newQty === 0) return 'red';
      if (newQty < 10) return 'orange';
      return 'green';
    },
    getTransactionPrefix() {
      if ([1, 7].includes(this.form.action_type)) {
        return "+";
      } else {
        return "-";
      }
    },
    getOldTransactionPrefix() {
      if ([1, 7].includes(this.originalForm.action_type)) {
        return "+";
      } else {
        return "-";
      }
    },
    getTransactionColor() {
      if ([1, 7].includes(this.form.action_type)) {
        return "green";
      } else {
        return "red";
      }
    },
    getActionTypeLabel() {
      const item = this.actionTypeItems.find(a => a.value === this.form.action_type);
      return item ? item.text : "";
    },
    formatDate(date) {
      return date ? moment(date).format("DD MMM,YYYY HH:mm:ss") : "";
    },
    async save() {
      try {
        this.isLoading = true;

        // Clear previous errors
        this.errors = {};

        // Client-side validation
        let hasErrors = false;
        if (!this.selectedProduct) {
          this.$set(this.errors, 'product_id', ['Please select a product']);
          hasErrors = true;
        }
        if (!this.form.variant_id) {
          this.$set(this.errors, 'variant_id', ['Please select a product variant']);
          hasErrors = true;
        }
        if (!this.form.action_type) {
          this.$set(this.errors, 'action_type', ['Please select an action type']);
          hasErrors = true;
        }
        if (!this.form.quantity || this.form.quantity <= 0) {
          this.$set(this.errors, 'quantity', ['Quantity must be greater than 0']);
          hasErrors = true;
        }

        if (hasErrors) {
          this.isLoading = false;
          return;
        }

        let encryptedId = this.$route.params.id;
        await axios.patch(`/stock_logs/${encryptedId}`, this.form);
        this.isLoading = false;
        this.is_updated = true;
      } catch (e) {
        this.isLoading = false;
        if (e.response && e.response.data && e.response.data.errors) {
          this.errors = e.response.data.errors;
        } else {
          console.error(e);
        }
      }
    },
    deleteLog() {
      this.showDeleteDialog = true;
    },
    async confirmDelete() {
      try {
        this.showDeleteDialog = false;
        this.isDeleting = true;
        let encryptedId = this.$route.params.id;
        await axios.post(`/stock_logs/delete/${encryptedId}`);
        this.isDeleting = false;
        this.$router.push("/stock_logs");
      } catch (e) {
        this.isDeleting = false;
        console.error(e);
      }
    },
    handleSuccessClose() {
      this.is_updated = false;
      this.$router.push("/stock_logs");
    }
  },
  watch: {
    'form.quantity': function(newVal) {
      // Clear quantity error when valid quantity is entered
      if (newVal && newVal > 0 && this.errors.quantity) {
        this.$set(this.errors, 'quantity', null);
      }
    },
    'form.action_type': function(newVal) {
      // Clear action_type error when selected
      if (newVal && this.errors.action_type) {
        this.$set(this.errors, 'action_type', null);
      }
    }
  }
};
</script>